import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Trash2, Search, Users, Crown, Bot, Mail, Flag, X, Calendar, Check, UserX, LogOut, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AdminBlog from "../components/AdminBlog";

const StatPill = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="flex-1 bg-white rounded-2xl border border-kora-line p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-full bg-kora-terracotta/10 text-kora-terracotta flex items-center justify-center">
      <Icon size={18} />
    </div>
    <div>
      <p className="text-xl font-display font-black text-kora-ink leading-none">{value}</p>
      <p className="text-xs text-kora-muted mt-1">{label}</p>
    </div>
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "—";
  }
};

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-kora-line/60 last:border-0">
    <span className="text-xs text-kora-muted shrink-0">{label}</span>
    <span className="text-xs font-semibold text-kora-ink text-right">{value || "—"}</span>
  </div>
);

const Admin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [confirmUser, setConfirmUser] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [deletions, setDeletions] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("membres");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
      try {
        const del = await api.get("/admin/deletions");
        setDeletions(del.data);
      } catch (e) {}
    } catch (e) {
      toast.error("Accès refusé ou erreur de chargement.");
      navigate("/profil");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const doDelete = async () => {
    if (!confirmUser || busy) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${confirmUser.user_id}`);
      toast.success(`${confirmUser.name || "Compte"} supprimé.`);
      setUsers((list) => list.filter((u) => u.user_id !== confirmUser.user_id));
      setSelected((s) => s.filter((id) => id !== confirmUser.user_id));
      setConfirmUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible.");
    }
    setBusy(false);
  };

  const openDetail = async (u) => {
    setDetailLoading(true);
    setDetail({ user: u, reports: [] });
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}`);
      setDetail(data);
    } catch (e) {
      toast.error("Impossible de charger les détails.");
      setDetail(null);
    }
    setDetailLoading(false);
  };

  const togglePremium = async (u, grant) => {
    if (busy) return;
    setBusy(true);
    try {
      if (grant) {
        const { data } = await api.post(`/admin/users/${u.user_id}/premium`);
        toast.success(`Premium actif jusqu'au ${fmtDate(data.premium_until)} 👑`);
        setDetail((d) => (d ? { ...d, user: { ...d.user, premium: true, premium_until: data.premium_until } } : d));
        setUsers((list) => list.map((x) => (x.user_id === u.user_id ? { ...x, premium: true } : x)));
      } else {
        await api.delete(`/admin/users/${u.user_id}/premium`);
        toast.success("Premium retiré.");
        setDetail((d) => (d ? { ...d, user: { ...d.user, premium: false, premium_until: null } } : d));
        setUsers((list) => list.map((x) => (x.user_id === u.user_id ? { ...x, premium: false } : x)));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible.");
    }
    setBusy(false);
  };

  const moderate = async (u, action) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/${action}`);
      setUsers((list) => list.map((x) => (x.user_id === u.user_id ? { ...x, verified: data.verified, rejected: data.rejected } : x)));
      toast.success(action === "approve" ? `${u.name || "Profil"} approuvé — le membre a été notifié.` : `${u.name || "Profil"} rejeté — le membre a été notifié.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible.");
    }
  };

  const toggleSelect = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const sendMessage = async () => {
    if (sending) return;
    if (!subject.trim() || !message.trim()) {
      toast.error("Sujet et message sont requis.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/message", { user_ids: selected, subject, message });
      toast.success(`Email envoyé à ${data.sent} membre${data.sent > 1 ? "s" : ""}.`);
      if (data.failed?.length) toast.warning(`Non envoyé : ${data.failed.join(", ")}`);
      setMsgOpen(false);
      setSubject("");
      setMessage("");
      setSelected([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible.");
    }
    setSending(false);
  };

  const filtered = users.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.city || "").toLowerCase().includes(q)
    );
  });

  const realCount = users.filter((u) => !u.is_fake).length;
  const premiumCount = users.filter((u) => u.premium).length;
  const fakeCount = users.filter((u) => u.is_fake).length;
  const du = detail?.user;

  return (
    <div className="min-h-screen bg-kora-sand pb-28">
      <header className="bg-kora-ink text-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button data-testid="admin-back" onClick={() => navigate("/profil")} className="text-white/90">
            <ArrowLeft size={22} />
          </button>
          <Shield size={20} className="text-kora-ochre" />
          <div>
            <h1 className="font-display font-black text-lg leading-tight">Espace administrateur</h1>
            <p className="text-xs text-white/60">Gestion des comptes KoraLove</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-4 flex gap-2">
        <button
          data-testid="admin-tab-membres"
          onClick={() => setTab("membres")}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-colors ${tab === "membres" ? "bg-kora-ink text-white" : "bg-white border border-kora-line text-kora-ink"}`}
        >
          <Users size={15} /> Membres
        </button>
        <button
          data-testid="admin-tab-blog"
          onClick={() => setTab("blog")}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-colors ${tab === "blog" ? "bg-kora-ink text-white" : "bg-white border border-kora-line text-kora-ink"}`}
        >
          <FileText size={15} /> Blog
        </button>
      </div>

      {tab === "blog" ? (
        <AdminBlog />
      ) : (
      <>
      <div className="max-w-3xl mx-auto px-4 py-5">
        <div className="flex gap-3 mb-5">
          <StatPill icon={Users} label="Membres réels" value={realCount} testid="stat-real" />
          <StatPill icon={Crown} label="Premium" value={premiumCount} testid="stat-premium" />
          <StatPill icon={Bot} label="Faux profils" value={fakeCount} testid="stat-fake" />
          <StatPill icon={UserX} label="Comptes supprimés" value={deletions ? deletions.total : "…"} testid="stat-deletions" />
        </div>

        <div className="relative mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-kora-muted" />
          <input
            data-testid="admin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher par nom, email ou ville…"
            className="w-full pl-10 pr-4 py-3 rounded-full border border-kora-line bg-white focus:outline-none focus:border-kora-terracotta text-sm"
          />
        </div>

        {loading ? (
          <div className="py-16 flex justify-center">
            <div className="w-10 h-10 border-4 border-kora-terracotta border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-user-list">
            {filtered.length === 0 && (
              <p className="text-center text-kora-muted text-sm py-10">Aucun compte trouvé.</p>
            )}
            {filtered.map((u) => (
              <div
                key={u.user_id}
                data-testid={`admin-user-${u.user_id}`}
                onClick={() => openDetail(u)}
                className="bg-white rounded-2xl border border-kora-line p-3 flex items-center gap-3 cursor-pointer hover:border-kora-terracotta/50 transition-colors"
              >
                <input
                  type="checkbox"
                  data-testid={`admin-select-${u.user_id}`}
                  checked={selected.includes(u.user_id)}
                  onChange={() => toggleSelect(u.user_id)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-5 h-5 shrink-0 accent-[#E05D36] cursor-pointer"
                />
                <img
                  src={u.photo_url ? resolveImg(u.photo_url) : "https://ui-avatars.com/api/?name=" + encodeURIComponent(u.name || "?")}
                  alt={u.name}
                  className="w-12 h-12 rounded-full object-cover bg-kora-sand shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-bold text-kora-ink truncate">{u.name || "Sans nom"}</p>
                    {u.online && (
                      <span data-testid={`admin-online-${u.user_id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> EN LIGNE
                      </span>
                    )}
                    {u.is_admin && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-kora-ink text-white">ADMIN</span>}
                    {u.premium && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-kora-ochre/20 text-[#C77826]">PREMIUM</span>}
                    {u.is_fake && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-kora-sage/20 text-kora-sage">FAUX</span>}
                    {!u.is_fake && !u.is_admin && u.onboarded && !u.verified && !u.rejected && (
                      <span data-testid={`admin-pending-${u.user_id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600">EN ATTENTE</span>
                    )}
                    {u.rejected && <span data-testid={`admin-rejected-${u.user_id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">REJETÉ</span>}
                    {u.reports_count > 0 && (
                      <span data-testid={`admin-reported-${u.user_id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 inline-flex items-center gap-0.5">
                        <Flag size={9} /> SIGNALÉ ({u.reports_count})
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-kora-muted truncate">{u.email || "—"}</p>
                  <p className="text-xs text-kora-muted/80 truncate">
                    {[u.city, u.age ? `${u.age} ans` : null, u.gender].filter(Boolean).join(" · ") || "Profil incomplet"}
                  </p>
                  <p className="text-[11px] text-kora-muted/70 truncate inline-flex items-center gap-1">
                    <Calendar size={10} /> Inscrit le {fmtDate(u.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {u.online && (
                    <button
                      data-testid={`admin-disconnect-${u.user_id}`}
                      title="Déconnecter cet utilisateur (il n'apparaîtra plus en ligne)"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await api.post(`/admin/users/${u.user_id}/disconnect`);
                          setUsers((list) => list.map((x) => (x.user_id === u.user_id ? { ...x, online: false } : x)));
                          toast.success(`${u.name || "Utilisateur"} déconnecté ✔ Il n'apparaît plus en ligne.`);
                        } catch (err) {
                          toast.error("Échec de la déconnexion.");
                        }
                      }}
                      className="w-10 h-10 rounded-full bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-600 hover:text-white transition-colors relative"
                    >
                      <LogOut size={17} />
                      <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white" />
                    </button>
                  )}
                  {!u.is_fake && !u.is_admin && u.onboarded && (
                    <>
                      {!u.verified && (
                        <button
                          data-testid={`admin-approve-${u.user_id}`}
                          title="Approuver le profil"
                          onClick={(e) => { e.stopPropagation(); moderate(u, "approve"); }}
                          className="w-10 h-10 rounded-full bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-600 hover:text-white transition-colors"
                        >
                          <Check size={18} />
                        </button>
                      )}
                      {!u.rejected && (
                        <button
                          data-testid={`admin-reject-${u.user_id}`}
                          title="Rejeter le profil"
                          onClick={(e) => { e.stopPropagation(); moderate(u, "reject"); }}
                          className="w-10 h-10 rounded-full bg-orange-50 text-orange-500 flex items-center justify-center hover:bg-orange-500 hover:text-white transition-colors"
                        >
                          <UserX size={18} />
                        </button>
                      )}
                    </>
                  )}
                  <button
                    data-testid={`admin-delete-${u.user_id}`}
                    disabled={u.is_admin}
                    onClick={(e) => { e.stopPropagation(); setConfirmUser(u); }}
                    className="w-10 h-10 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors disabled:opacity-30 disabled:hover:bg-red-50 disabled:hover:text-red-500"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-[70] bg-kora-ink text-white shadow-2xl" data-testid="admin-selection-bar">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
            <p className="text-sm font-semibold flex-1">
              {selected.length} profil{selected.length > 1 ? "s" : ""} sélectionné{selected.length > 1 ? "s" : ""}
            </p>
            <button data-testid="admin-clear-selection" onClick={() => setSelected([])} className="text-white/70 text-sm underline">
              Annuler
            </button>
            <button
              data-testid="admin-open-message"
              onClick={() => setMsgOpen(true)}
              className="inline-flex items-center gap-2 bg-kora-terracotta px-4 py-2.5 rounded-full font-bold text-sm"
            >
              <Mail size={16} /> Envoyer un message
            </button>
          </div>
        </div>
      )}
      </>
      )}

      {msgOpen && (
        <div className="fixed inset-0 z-[85] bg-kora-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="admin-message-modal" onClick={() => setMsgOpen(false)}>
          <div className="bg-white w-full md:max-w-md rounded-3xl p-6 animate-float-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Mail size={20} className="text-kora-terracotta" />
              <h3 className="font-display font-black text-lg text-kora-ink">Envoyer un email</h3>
            </div>
            <p className="text-xs text-kora-muted mb-4">
              À {selected.length} membre{selected.length > 1 ? "s" : ""}, depuis l'adresse KoraLove officielle.
            </p>
            <input
              data-testid="admin-message-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Sujet de l'email"
              className="w-full px-4 py-3 rounded-2xl border border-kora-line focus:outline-none focus:border-kora-terracotta text-sm mb-3"
            />
            <textarea
              data-testid="admin-message-body"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Votre message…"
              rows={5}
              className="w-full px-4 py-3 rounded-2xl border border-kora-line focus:outline-none focus:border-kora-terracotta text-sm mb-4 resize-none"
            />
            <div className="flex gap-2">
              <button data-testid="admin-message-cancel" onClick={() => setMsgOpen(false)} className="flex-1 py-2.5 rounded-full border border-kora-line font-semibold text-kora-ink">Annuler</button>
              <button data-testid="admin-message-send" onClick={sendMessage} disabled={sending} className="flex-1 py-2.5 rounded-full bg-kora-terracotta text-white font-bold disabled:opacity-60">
                {sending ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[80] bg-kora-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="admin-detail-modal" onClick={() => setDetail(null)}>
          <div className="bg-white w-full md:max-w-md rounded-3xl p-6 animate-float-up max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <img
                  src={du?.photo_url ? resolveImg(du.photo_url) : "https://ui-avatars.com/api/?name=" + encodeURIComponent(du?.name || "?")}
                  alt={du?.name}
                  className="w-16 h-16 rounded-full object-cover bg-kora-sand"
                />
                <div>
                  <h3 className="font-display font-black text-lg text-kora-ink">{du?.name || "Sans nom"}</h3>
                  <p className="text-xs text-kora-muted">{du?.email}</p>
                </div>
              </div>
              <button data-testid="admin-detail-close" onClick={() => setDetail(null)} className="text-kora-muted hover:text-kora-ink">
                <X size={20} />
              </button>
            </div>

            {detailLoading ? (
              <div className="py-10 flex justify-center">
                <div className="w-8 h-8 border-4 border-kora-terracotta border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="bg-kora-sand/60 rounded-2xl p-4 mb-4">
                  <DetailRow label="Inscrit le" value={fmtDate(du?.created_at)} />
                  <DetailRow label="Dernière activité" value={fmtDate(du?.last_active)} />
                  <DetailRow label="Âge" value={du?.age ? `${du.age} ans` : null} />
                  <DetailRow label="Genre" value={du?.gender} />
                  <DetailRow label="Pays d'origine" value={du?.city} />
                  <DetailRow label="Pays de résidence" value={du?.residence_country} />
                  <DetailRow label="But" value={du?.goal === "mariage" ? "Mariage" : du?.goal === "amical" ? "Amitié" : du?.goal} />
                  <DetailRow label="Recherche" value={du?.interested_in} />
                  <DetailRow label="Situation" value={du?.marital_status} />
                  <DetailRow label="Enfants" value={du?.has_children} />
                  <DetailRow label="Études" value={du?.education} />
                  <DetailRow label="Taille" value={du?.height ? `${du.height} cm` : null} />
                  <DetailRow label="Premium" value={du?.premium ? `Oui${du?.premium_until ? ` — jusqu'au ${fmtDate(du.premium_until)}` : ""}` : "Non"} />
                  <DetailRow label="Vérifié" value={du?.verified ? "Oui" : "Non"} />
                  <DetailRow label="Inscription terminée" value={du?.onboarded ? "Oui" : "Non"} />
                  <DetailRow label="Intérêts" value={(du?.interests || []).join(", ")} />
                </div>
                <div className="mb-4">
                  {du?.premium ? (
                    <div className="flex gap-2">
                      <button
                        data-testid="admin-extend-premium-btn"
                        onClick={() => togglePremium(du, true)}
                        disabled={busy}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-kora-ochre/20 text-[#9A6A1F] font-bold text-sm disabled:opacity-60"
                      >
                        <Crown size={15} /> Prolonger de 30 jours
                      </button>
                      <button
                        data-testid="admin-revoke-premium-btn"
                        onClick={() => togglePremium(du, false)}
                        disabled={busy}
                        className="flex-1 py-2.5 rounded-full border border-kora-line text-kora-ink font-semibold text-sm disabled:opacity-60"
                      >
                        Retirer Premium
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid="admin-grant-premium-btn"
                      onClick={() => togglePremium(du, true)}
                      disabled={busy}
                      className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-kora-terracotta text-white font-bold text-sm disabled:opacity-60"
                    >
                      <Crown size={15} /> Offrir Premium (30 jours)
                    </button>
                  )}
                </div>
                {du?.bio && (
                  <div className="mb-4">
                    <p className="text-xs font-bold text-kora-muted mb-1">BIO</p>
                    <p className="text-sm text-kora-ink/80 leading-relaxed">{du.bio}</p>
                  </div>
                )}
                <div data-testid="admin-detail-reports">
                  <p className="text-xs font-bold text-kora-muted mb-2 inline-flex items-center gap-1">
                    <Flag size={12} className={detail.reports?.length ? "text-red-500" : ""} /> SIGNALEMENTS ({detail.reports?.length || 0})
                  </p>
                  {(detail.reports || []).length === 0 ? (
                    <p className="text-xs text-kora-muted">Ce profil n'a jamais été signalé.</p>
                  ) : (
                    <div className="space-y-2">
                      {detail.reports.map((r) => (
                        <div key={r.report_id} className="bg-red-50 border border-red-100 rounded-xl p-3">
                          <p className="text-xs font-semibold text-red-700">{r.reason || "Sans motif précisé"}</p>
                          <p className="text-[11px] text-red-400 mt-0.5">Par {r.reporter_name} · {fmtDate(r.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {confirmUser && (
        <div className="fixed inset-0 z-[90] bg-kora-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="admin-delete-modal" onClick={() => setConfirmUser(null)}>
          <div className="bg-white w-full md:max-w-sm rounded-3xl p-6 animate-float-up text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-3">
              <Trash2 size={26} />
            </div>
            <h3 className="font-display font-black text-lg text-kora-ink">Supprimer {confirmUser.name} ?</h3>
            <p className="text-sm text-kora-muted mt-1 mb-5">Cette action est définitive. Le compte et toutes ses données (matchs, messages, likes, visites) seront supprimés.</p>
            <div className="flex gap-2">
              <button data-testid="admin-delete-cancel" onClick={() => setConfirmUser(null)} className="flex-1 py-2.5 rounded-full border border-kora-line font-semibold text-kora-ink">Annuler</button>
              <button data-testid="admin-delete-confirm" onClick={doDelete} disabled={busy} className="flex-1 py-2.5 rounded-full bg-red-500 text-white font-bold disabled:opacity-60">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
